import type { LucideIcon } from "lucide-react";
import {
  ArrowLeft,
  Building2,
  Calendar,
  CircleDollarSign,
  FileText,
  Key,
  Landmark,
  Paperclip,
  QrCode,
  Send,
  Truck,
  User,
  UserCheck,
  Wallet,
} from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { formatCurrency, formatDate } from "@/utils/format";
import type { SolicitacaoPagamentoFormValues } from "@/services/solicitacaoPagamento";
import { parseValorInput } from "./formUtils";
import { pixTipoChaveLabel } from "./pagamentoMetodoUtils";

interface Props {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  values: SolicitacaoPagamentoFormValues;
  solicitanteLabel: string;
  aprovadorLabel: string;
  loading?: boolean;
  existingAnexoName?: string | null;
  mode?: "create" | "edit";
}

function ReviewFieldCard({
  icon: Icon,
  label,
  value,
  valueClassName,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  valueClassName?: string;
}) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white px-3.5 py-3">
      <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-brand-50 flex items-center justify-center">
        <Icon className="w-4 h-4 text-brand-600" strokeWidth={2} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</p>
        <p className={`text-sm text-slate-900 break-words mt-0.5 ${valueClassName ?? ""}`}>
          {value || "—"}
        </p>
      </div>
    </div>
  );
}

function formaPagamentoLabel(fp: SolicitacaoPagamentoFormValues["formaPagamento"]): string {
  if (fp === "PIX") return "PIX";
  if (fp === "BOLETO") return "Boleto";
  if (fp === "TRANSFERENCIA") return "Transferência";
  return fp || "—";
}

export function SolicitacaoPagamentoReviewModal({
  open,
  onClose,
  onConfirm,
  values,
  aprovadorLabel,
  loading,
  existingAnexoName,
  mode = "create",
}: Props) {
  const valor = parseValorInput(values.valor);
  const isEdit = mode === "edit";
  const anexoLabel =
    values.anexoFile?.name ?? existingAnexoName?.trim() ?? "Nenhum";

  const leftColumn = (
    <div className="flex flex-col gap-3">
      <ReviewFieldCard icon={UserCheck} label="Enviar para aprovação" value={aprovadorLabel} />
      <ReviewFieldCard icon={Building2} label="Centro de custo" value={values.costCenterLabel} />
      <ReviewFieldCard icon={FileText} label="Plano financeiro" value={values.paymentCategoryLabel} />
      <ReviewFieldCard icon={Truck} label="Fornecedor" value={values.creditorName} />
      <ReviewFieldCard icon={FileText} label="Descrição" value={values.descricao} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <ReviewFieldCard
          icon={CircleDollarSign}
          label="Valor"
          value={valor != null ? formatCurrency(valor) : values.valor}
          valueClassName="font-bold text-emerald-600"
        />
        <ReviewFieldCard
          icon={Wallet}
          label="Forma de pagamento"
          value={formaPagamentoLabel(values.formaPagamento)}
        />
      </div>
    </div>
  );

  const rightFields: Array<{ icon: LucideIcon; label: string; value: string }> = [];

  if (values.formaPagamento === "PIX") {
    rightFields.push(
      { icon: Key, label: "Tipo chave", value: pixTipoChaveLabel(values.pixTipoChave) },
      { icon: QrCode, label: "Chave Pix", value: values.pixChave },
      { icon: User, label: "Conta em nome", value: values.pixContaEmNome },
    );
  } else if (values.formaPagamento === "BOLETO") {
    rightFields.push({
      icon: FileText,
      label: "Código de barras",
      value: values.boletoCodigoBarras,
    });
  } else if (values.formaPagamento === "TRANSFERENCIA") {
    rightFields.push(
      { icon: User, label: "Titular", value: values.transferenciaTitular },
      { icon: Landmark, label: "Banco", value: values.transferenciaBanco },
      { icon: Building2, label: "Agência", value: values.transferenciaAgencia },
      { icon: Wallet, label: "Conta", value: values.transferenciaConta },
    );
  }

  rightFields.push(
    {
      icon: Calendar,
      label: "Vencimento",
      value: values.dueDate ? formatDate(values.dueDate) : "",
    },
    { icon: Paperclip, label: "Anexo", value: anexoLabel },
  );

  const modalTitle = (
    <div className="flex items-center gap-3 min-w-0">
      <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-brand-50 flex items-center justify-center">
        <Send className="w-4 h-4 text-brand-600" strokeWidth={2} />
      </div>
      <span className="font-bold text-brand-900 truncate">
        {isEdit ? "Revisar e reenviar solicitação" : "Revisar solicitação"}
      </span>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={modalTitle}
      size="lg"
      footer={
        <div className="flex items-center justify-between gap-3 w-full">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={loading}
            className="bg-white border border-slate-200 text-brand-600 hover:bg-slate-50 hover:text-brand-700"
            leftIcon={<ArrowLeft className="w-4 h-4" />}
          >
            Voltar
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            loading={loading}
            leftIcon={<Send className="w-4 h-4" />}
          >
            {isEdit ? "Confirmar reenvio" : "Enviar solicitação"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {leftColumn}
          <div className="flex flex-col gap-3">
            {rightFields.map((field) => (
              <ReviewFieldCard
                key={field.label}
                icon={field.icon}
                label={field.label}
                value={field.value}
              />
            ))}
          </div>
        </div>

        {values.observacao?.trim() && (
          <ReviewFieldCard icon={FileText} label="Observação" value={values.observacao} />
        )}

        <div className="flex items-center gap-2.5 rounded-xl bg-sky-50 border border-sky-100 px-4 py-3">
          <Calendar className="w-4 h-4 text-brand-600 flex-shrink-0" />
          <p className="text-sm font-medium text-brand-600">
            Enviado em {formatDate(new Date().toISOString())}
          </p>
        </div>
      </div>
    </Modal>
  );
}
